import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Field, Form as FinalForm } from 'react-final-form';
import {
  Button,
  Form,
  FormField,
  Image,
  Label,
  Modal,
  ModalActions,
  ModalContent,
  ModalHeader,
} from 'semantic-ui-react';
import ValidatedFormInput from 'components/ValidatedFormInput';
import validator from 'validator';
import { useSelector } from 'react-redux';
import ImageUploader from 'components/ImageUploader';
import { readFileAsDataUrl } from 'utils/fileUtils';
import parseDataUrl from 'parse-data-url';

const formMutatorObject = {
  setValue: ([field, value], state, { changeValue }) =>
    changeValue(state, field, () => value),
};

const numericOnlyNormalization = value => {
  if (!value) {
    return '';
  }
  return value.replace(/[^\d]/g, '');
};

const validateImageUploaded = initialImage => value =>
  !value && !initialImage ? 'Image is required' : undefined;

const requiredStringValidation = (value = '') =>
  validator.isEmpty(value.trim()) ? 'This field is required' : undefined;

function EditKtpModal({ shown, onClose, onSubmit, isLoading }) {
  let submit;
  let formMutators;
  let formInitialize;

  const initialValues = useSelector(state => state.kyb.businessDetail.ktp);
  const [imagePreview, setImagePreview] = useState(null);

  useEffect(() => {
    if (shown) {
      const obj = initialValues || {};
      formInitialize({
        ktp_no: obj.number,
        ktp_name: obj.name,
        ktp_image: null,
      });
      setImagePreview(null);
    }
  }, [formInitialize, initialValues, shown]);

  const onFileDrop = async files => {
    const url = await readFileAsDataUrl(files[0]);
    const parsed = parseDataUrl(url);
    setImagePreview(url);
    formMutators.setValue('ktp_image', parsed.data);
  };

  const renderForm = ({ handleSubmit, form: { mutators, initialize } }) => {
    submit = handleSubmit;
    formMutators = mutators;
    formInitialize = initialize;
    return (
      <Form>
        <FormField>
          <label htmlFor="ktp-number">KTP Number:</label>
          <Field
            id="ktp-number"
            name="ktp_no"
            component={ValidatedFormInput}
            parse={numericOnlyNormalization}
            validate={requiredStringValidation}
          />
        </FormField>
        <FormField>
          <label htmlFor="ktp-name">KTP Name:</label>
          <Field
            id="ktp-name"
            name="ktp_name"
            component={ValidatedFormInput}
            validate={requiredStringValidation}
          />
        </FormField>
        <FormField>
          <label htmlFor="ktp-image">KTP Image:</label>
          {(!!initialValues.image || !!imagePreview) && (
            <Image src={imagePreview || initialValues.image} size="medium" />
          )}
          <ImageUploader onChange={onFileDrop} />
          <Field
            name="ktp_image"
            validate={validateImageUploaded(initialValues.image)}
          >
            {({ meta: { touched, error } }) => {
              const errorContent = touched && !!error && error;
              return errorContent && <Label prompt content={errorContent} />;
            }}
          </Field>
        </FormField>
      </Form>
    );
  };

  renderForm.propTypes = {
    handleSubmit: PropTypes.func.isRequired,
    form: PropTypes.object.isRequired,
  };

  return (
    <Modal open={shown}>
      <ModalHeader>Edit KTP Details</ModalHeader>
      <ModalContent>
        <FinalForm
          onSubmit={onSubmit}
          mutators={formMutatorObject}
          render={renderForm}
        />
      </ModalContent>
      <ModalActions>
        <Button onClick={onClose} disabled={isLoading}>
          Cancel
        </Button>
        <Button
          positive
          onClick={event => submit(event)}
          disabled={isLoading}
          loading={isLoading}
        >
          Submit
        </Button>
      </ModalActions>
    </Modal>
  );
}

EditKtpModal.propTypes = {
  shown: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  isLoading: PropTypes.bool.isRequired,
};

export default EditKtpModal;
